import type { ReactNode } from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';

import type { CommandMultiSelectProps } from '../../components/CommandMultiSelect';
import { CommandMultiSelect } from '../../components/CommandMultiSelect';
import type { CommandSelectOption, CommandSelectOptionId } from '../../components/CommandSelect';

export interface RHFCommandMultiSelectProps<TFieldValues extends FieldValues>
    extends Omit<CommandMultiSelectProps, 'value' | 'defaultValue' | 'onChange' | 'name' | 'errorMessage' | 'onBlur'> {
    name: FieldPath<TFieldValues>;
    control?: Control<TFieldValues>;
    dataTestid?: string;
    label?: ReactNode;
    onAfterChange?: (nextIds: CommandSelectOptionId[], nextOptions: CommandSelectOption[]) => void;
    /**
     * Si true, stocke les objets complets (CommandSelectOption[]) dans RHF au lieu des IDs.
     * Utile pour préserver les labels des options créées à la volée.
     */
    returnObjects?: boolean;
}

export function RHFCommandMultiSelect<TFieldValues extends FieldValues>({
    name,
    control,
    dataTestid,
    returnObjects = false,
    onAfterChange,
    ...props
}: RHFCommandMultiSelectProps<TFieldValues>) {
    const { field, fieldState } = useController({
        control,
        name,
    });
    const valueAsIds: CommandSelectOptionId[] = (() => {
        if (returnObjects) {
            const { value } = field;
            if (props.mode === 'single') {
                if (!value) return [];
                const { id } = value as CommandSelectOption;
                return [id];
            }
            if (!Array.isArray(value)) return [];
            return (value as CommandSelectOption[]).map((o) => o.id);
        }
        if (props.mode === 'single') {
            if (Array.isArray(field.value)) {
                return (field.value as CommandSelectOptionId[]).slice(0, 1);
            }
            return field.value !== null && field.value !== undefined ? ([field.value] as CommandSelectOptionId[]) : [];
        }
        return Array.isArray(field.value) ? (field.value as CommandSelectOptionId[]) : [];
    })();
    return (
        <div className='w-full'>
            <CommandMultiSelect
                {...props}
                dataTestid={dataTestid}
                value={valueAsIds}
                onChange={(nextIds, nextOptions) => {
                    if (returnObjects) {
                        if (props.mode === 'single') {
                            field.onChange(nextOptions[0] ?? null);
                        } else {
                            field.onChange(nextOptions);
                        }
                    } else {
                        if (props.mode === 'single') {
                            field.onChange(nextIds[0] ?? null);
                        } else {
                            field.onChange(nextIds);
                        }
                    }
                    onAfterChange?.(nextIds, nextOptions);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
            />
        </div>
    );
}
